import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { ipdApi } from "@/api/ipd";
import { superAdminApi } from "@/api/superAdmin";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { FileText, Pencil, Plus, Search, Trash2, Upload } from "lucide-react";

type LabExternalVendor = {
  id: string;
  name: string;
  phone?: string | null;
  address?: string | null;
  gstNo?: string | null;
  email?: string | null;
  createdAt?: string;
};

const emptyVendorForm = {
  name: "",
  phone: "",
  address: "",
  gstNo: "",
  email: "",
};

const emptyInvoiceForm = {
  invoiceNumber: "",
  invoiceDate: "",
  totalAmount: "",
  notes: "",
};

export default function LabExternalVendorsPage() {
  const qc = useQueryClient();
  const { user, activeHospitalId, isChainAdmin } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const showHospitalFilter = isSuperAdmin || isChainAdmin;
  const chainHospitals = user?.hospitals ?? [];

  const [selectedHospitalId, setSelectedHospitalId] = useState(
    activeHospitalId || user?.hospitalId || chainHospitals[0]?.id || ""
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<LabExternalVendor | null>(null);
  const [vendorForm, setVendorForm] = useState(emptyVendorForm);
  const [deletingVendorId, setDeletingVendorId] = useState<string | null>(null);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceVendor, setInvoiceVendor] = useState<LabExternalVendor | null>(null);
  const [invoiceForm, setInvoiceForm] = useState(emptyInvoiceForm);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);

  const { data: superHospitals = [] } = useQuery({
    queryKey: ["super-admin-hospitals"],
    queryFn: () => superAdminApi.getHospitals(),
    enabled: isSuperAdmin,
  });

  const hospitalOptions = useMemo(() => {
    if (isSuperAdmin) {
      return superHospitals as Array<{ id: string; name: string }>;
    }
    return chainHospitals;
  }, [isSuperAdmin, superHospitals, chainHospitals]);

  useEffect(() => {
    if (!showHospitalFilter) return;
    if (selectedHospitalId) return;
    const fallback = activeHospitalId || chainHospitals[0]?.id || hospitalOptions[0]?.id || "";
    if (fallback) setSelectedHospitalId(fallback);
  }, [
    showHospitalFilter,
    selectedHospitalId,
    activeHospitalId,
    chainHospitals,
    hospitalOptions,
  ]);

  const effectiveHospitalId = showHospitalFilter
    ? selectedHospitalId
    : user?.hospitalId || activeHospitalId || "";

  const hospitalParams = effectiveHospitalId ? { hospitalId: effectiveHospitalId } : undefined;

  const { data: vendors = [], isLoading } = useQuery({
    queryKey: ["lab-external-vendors-admin", effectiveHospitalId],
    queryFn: async () => {
      const res = await ipdApi.listLabExternalVendors(hospitalParams);
      return (res.data?.data ?? []) as LabExternalVendor[];
    },
    enabled: !!effectiveHospitalId,
  });

  const filteredVendors = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return vendors;
    return vendors.filter(
      (v) =>
        v.name.toLowerCase().includes(q) ||
        (v.phone ?? "").toLowerCase().includes(q) ||
        (v.gstNo ?? "").toLowerCase().includes(q) ||
        (v.email ?? "").toLowerCase().includes(q)
    );
  }, [vendors, searchQuery]);

  const invalidateVendorQueries = async () => {
    await qc.invalidateQueries({ queryKey: ["lab-external-vendors-admin"] });
    await qc.invalidateQueries({ queryKey: ["ipd-lab-external-vendors"] });
    await qc.invalidateQueries({ queryKey: ["lab-external-vendors"] });
  };

  const saveVendorMutation = useMutation({
    mutationFn: async () => {
      const name = vendorForm.name.trim();
      if (!name) throw new Error("Vendor name is required");
      if (!effectiveHospitalId) throw new Error("Select a hospital first");
      const payload = {
        name,
        phone: vendorForm.phone.trim() || undefined,
        address: vendorForm.address.trim() || undefined,
        gstNo: vendorForm.gstNo.trim() || undefined,
        email: vendorForm.email.trim() || undefined,
      };
      if (editingVendor) {
        return ipdApi.updateLabExternalVendor(editingVendor.id, payload, hospitalParams);
      }
      return ipdApi.createLabExternalVendor(payload, hospitalParams);
    },
    onSuccess: async () => {
      toast.success(editingVendor ? "External lab vendor updated" : "External lab vendor added");
      setVendorDialogOpen(false);
      setEditingVendor(null);
      setVendorForm(emptyVendorForm);
      await invalidateVendorQueries();
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          (editingVendor ? "Failed to update vendor" : "Failed to add vendor")
      );
    },
  });

  const deleteVendorMutation = useMutation({
    mutationFn: async (vendorId: string) => {
      if (!effectiveHospitalId) throw new Error("Select a hospital first");
      return ipdApi.deleteLabExternalVendor(vendorId, hospitalParams);
    },
    onSuccess: async () => {
      toast.success("External lab vendor deleted");
      await invalidateVendorQueries();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.message || "Failed to delete vendor");
    },
    onSettled: () => setDeletingVendorId(null),
  });

  const uploadInvoiceMutation = useMutation({
    mutationFn: async () => {
      if (!invoiceVendor?.id) throw new Error("Select a vendor");
      if (!effectiveHospitalId) throw new Error("Select a hospital first");
      const invoiceNumber = invoiceForm.invoiceNumber.trim();
      const totalAmount = Number(invoiceForm.totalAmount);
      if (!invoiceNumber) throw new Error("Invoice number is required");
      if (!Number.isFinite(totalAmount) || totalAmount <= 0) {
        throw new Error("Total amount must be greater than zero");
      }
      const fd = new FormData();
      fd.append("invoiceNumber", invoiceNumber);
      fd.append("totalAmount", String(totalAmount));
      if (invoiceForm.invoiceDate.trim()) {
        fd.append("invoiceDate", new Date(invoiceForm.invoiceDate).toISOString());
      }
      if (invoiceForm.notes.trim()) fd.append("notes", invoiceForm.notes.trim());
      if (invoiceFile) fd.append("file", invoiceFile);
      return ipdApi.createLabExternalVendorInvoice(invoiceVendor.id, fd, hospitalParams);
    },
    onSuccess: async () => {
      toast.success("Vendor invoice uploaded");
      setInvoiceDialogOpen(false);
      setInvoiceVendor(null);
      setInvoiceForm(emptyInvoiceForm);
      setInvoiceFile(null);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.message || "Failed to upload invoice");
    },
  });

  const openAddVendorDialog = () => {
    setEditingVendor(null);
    setVendorForm(emptyVendorForm);
    setVendorDialogOpen(true);
  };

  const openEditVendorDialog = (vendor: LabExternalVendor) => {
    setEditingVendor(vendor);
    setVendorForm({
      name: vendor.name,
      phone: vendor.phone ?? "",
      address: vendor.address ?? "",
      gstNo: vendor.gstNo ?? "",
      email: vendor.email ?? "",
    });
    setVendorDialogOpen(true);
  };

  const handleDeleteVendor = (vendor: LabExternalVendor) => {
    const ok = window.confirm(
      `Delete external lab vendor "${vendor.name}"?\n\nThis cannot be undone. Vendors with uploaded invoices cannot be deleted.`
    );
    if (!ok) return;
    setDeletingVendorId(vendor.id);
    deleteVendorMutation.mutate(vendor.id);
  };

  const openInvoiceDialog = (vendor: LabExternalVendor) => {
    setInvoiceVendor(vendor);
    setInvoiceForm({
      ...emptyInvoiceForm,
      invoiceDate: new Date().toISOString().slice(0, 10),
    });
    setInvoiceFile(null);
    setInvoiceDialogOpen(true);
  };

  const selectedHospitalName =
    hospitalOptions.find((h) => h.id === effectiveHospitalId)?.name ?? "Selected hospital";

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>External lab vendors</CardTitle>
            <CardDescription className="mt-1 max-w-2xl">
              Manage outsourced lab partners used when lab tests are marked{" "}
              <strong>External</strong> on OPD or IPD orders. Vendors are scoped per hospital unit.
            </CardDescription>
          </div>
          <Button
            onClick={openAddVendorDialog}
            disabled={!effectiveHospitalId}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add vendor
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            {showHospitalFilter ? (
              <div className="space-y-1 min-w-[240px]">
                <Label className="text-xs text-muted-foreground">Hospital unit</Label>
                <Select value={selectedHospitalId} onValueChange={setSelectedHospitalId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select hospital" />
                  </SelectTrigger>
                  <SelectContent>
                    {hospitalOptions.map((h) => (
                      <SelectItem key={h.id} value={h.id}>
                        {h.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                Hospital: <span className="font-medium text-foreground">{selectedHospitalName}</span>
              </div>
            )}

            <div className="relative w-full md:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, phone, GST, email"
                className="pl-9"
              />
            </div>
          </div>

          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>GST</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!effectiveHospitalId ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground">
                      Select a hospital to view vendors.
                    </TableCell>
                  </TableRow>
                ) : isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground">
                      Loading vendors…
                    </TableCell>
                  </TableRow>
                ) : filteredVendors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground">
                      No external lab vendors yet. Add one to use on outsourced lab tests.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredVendors.map((vendor) => (
                    <TableRow key={vendor.id}>
                      <TableCell className="font-medium">{vendor.name}</TableCell>
                      <TableCell>{vendor.phone || "—"}</TableCell>
                      <TableCell>{vendor.gstNo || "—"}</TableCell>
                      <TableCell>{vendor.email || "—"}</TableCell>
                      <TableCell className="max-w-[220px] truncate" title={vendor.address ?? ""}>
                        {vendor.address || "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => openEditVendorDialog(vendor)}
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => openInvoiceDialog(vendor)}
                          >
                            <Upload className="h-4 w-4 mr-1" />
                            Invoice
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            disabled={deletingVendorId === vendor.id}
                            onClick={() => handleDeleteVendor(vendor)}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            {deletingVendorId === vendor.id ? "Deleting…" : "Delete"}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-start gap-2 rounded-md border bg-muted/30 p-3 text-sm text-muted-foreground">
            <FileText className="h-4 w-4 mt-0.5 shrink-0" />
            <p>
              After adding vendors here, lab staff can pick them when ordering or completing
              external lab tests. Use <strong>Outside Lab Payout</strong> in Expenses to record
              payments to these vendors.
            </p>
          </div>
        </CardContent>
      </Card>

      <Dialog
        open={vendorDialogOpen}
        onOpenChange={(open) => {
          setVendorDialogOpen(open);
          if (!open) {
            setEditingVendor(null);
            setVendorForm(emptyVendorForm);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingVendor ? "Edit external lab vendor" : "Add external lab vendor"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {showHospitalFilter && (
              <div className="rounded-md border bg-muted/20 px-3 py-2 text-sm">
                Hospital: <span className="font-medium">{selectedHospitalName}</span>
              </div>
            )}
            <div className="space-y-1">
              <Label>Name *</Label>
              <Input
                value={vendorForm.name}
                onChange={(e) => setVendorForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. SRL Diagnostics"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Phone</Label>
                <Input
                  value={vendorForm.phone}
                  onChange={(e) => setVendorForm((p) => ({ ...p, phone: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label>GST No</Label>
                <Input
                  value={vendorForm.gstNo}
                  onChange={(e) => setVendorForm((p) => ({ ...p, gstNo: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Email</Label>
              <Input
                type="email"
                value={vendorForm.email}
                onChange={(e) => setVendorForm((p) => ({ ...p, email: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Address</Label>
              <Textarea
                rows={3}
                value={vendorForm.address}
                onChange={(e) => setVendorForm((p) => ({ ...p, address: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setVendorDialogOpen(false)}
              disabled={saveVendorMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => saveVendorMutation.mutate()}
              disabled={saveVendorMutation.isPending || !vendorForm.name.trim()}
            >
              {saveVendorMutation.isPending
                ? "Saving…"
                : editingVendor
                  ? "Save changes"
                  : "Save vendor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload vendor invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Vendor: <span className="font-medium text-foreground">{invoiceVendor?.name ?? "—"}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Invoice number *</Label>
                <Input
                  value={invoiceForm.invoiceNumber}
                  onChange={(e) =>
                    setInvoiceForm((p) => ({ ...p, invoiceNumber: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Invoice date</Label>
                <Input
                  type="date"
                  value={invoiceForm.invoiceDate}
                  onChange={(e) =>
                    setInvoiceForm((p) => ({ ...p, invoiceDate: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Total amount (₹) *</Label>
              <Input
                type="number"
                inputMode="decimal"
                value={invoiceForm.totalAmount}
                onChange={(e) =>
                  setInvoiceForm((p) => ({ ...p, totalAmount: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Textarea
                rows={2}
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Invoice file (PDF/image)</Label>
              <Input
                type="file"
                accept=".pdf,image/*"
                onChange={(e) => setInvoiceFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInvoiceDialogOpen(false)}
              disabled={uploadInvoiceMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => uploadInvoiceMutation.mutate()}
              disabled={uploadInvoiceMutation.isPending}
            >
              {uploadInvoiceMutation.isPending ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
